import { useMutation, useQueryClient } from '@tanstack/react-query'
import { client } from '../client'
import { queryKeys } from '../keys'
import type { RentalDetailed } from '../queries/rentals'

// Book a car
export function useBookCar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      accountName,
      vehicleId,
    }: {
      accountName: string
      vehicleId: number
    }) => {
      const { data, error } = await client.POST(
        '/api/v1/accounts/{account_name}/rentals',
        {
          params: { path: { account_name: accountName } },
          body: { vehicle_id: vehicleId },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: (data, { accountName }) => {
      // Update rental cache with new rental
      queryClient.setQueryData(queryKeys.rentals.current(accountName), data)
      // Invalidate related caches
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.status(accountName),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all })
    },
  })
}

// Cancel current rental
export function useCancelRental() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ accountName }: { accountName: string }) => {
      const { error } = await client.DELETE(
        '/api/v1/accounts/{account_name}/rentals/current',
        {
          params: { path: { account_name: accountName } },
        },
      )
      if (error) throw error
    },
    // Optimistic update - remove rental immediately
    onMutate: async ({ accountName }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.rentals.current(accountName),
      })

      // Snapshot previous value for rollback
      const previousRental = queryClient.getQueryData<RentalDetailed>(
        queryKeys.rentals.current(accountName),
      )

      // Optimistically remove rental
      queryClient.removeQueries({
        queryKey: queryKeys.rentals.current(accountName),
      })

      return { previousRental, accountName }
    },
    onError: (_err, { accountName }, context) => {
      // Rollback on error
      if (context?.previousRental) {
        queryClient.setQueryData(
          queryKeys.rentals.current(accountName),
          context.previousRental,
        )
      }
    },
    onSettled: (_data, _error, { accountName }) => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.status(accountName),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all })
    },
  })
}

// Extend current rental
export function useExtendRental() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ accountName }: { accountName: string }) => {
      const { data, error } = await client.PATCH(
        '/api/v1/accounts/{account_name}/rentals/current',
        {
          params: { path: { account_name: accountName } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: (_data, { accountName }) => {
      // Invalidate to refetch updated rental info
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentals.current(accountName),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.status(accountName),
      })
    },
  })
}

// Start trip (access vehicle)
export function useStartTrip() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ accountName }: { accountName: string }) => {
      const { data, error } = await client.POST(
        '/api/v1/accounts/{account_name}/rentals/current/start',
        {
          params: { path: { account_name: accountName } },
        },
      )
      if (error) throw error
      return data
    },
    // Optimistic update - set trip as started
    onMutate: async ({ accountName }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.rentals.current(accountName),
      })

      const previousRental = queryClient.getQueryData<RentalDetailed>(
        queryKeys.rentals.current(accountName),
      )

      if (previousRental) {
        // Optimistically update rental state
        queryClient.setQueryData(queryKeys.rentals.current(accountName), {
          ...previousRental,
          state: 'ongoing',
          usedStartDate: new Date().toISOString(),
        })
      }

      return { previousRental, accountName }
    },
    onError: (_err, { accountName }, context) => {
      // Rollback on error
      if (context?.previousRental) {
        queryClient.setQueryData(
          queryKeys.rentals.current(accountName),
          context.previousRental,
        )
      }
    },
    onSettled: (_data, _error, { accountName }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.status(accountName),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentals.current(accountName),
      })
    },
  })
}

// End trip
export function useEndTrip() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      accountName,
      moveToAnotherAccount,
    }: {
      accountName: string
      moveToAnotherAccount?: string
    }) => {
      const { data, error } = await client.POST(
        '/api/v1/accounts/{account_name}/rentals/current/end',
        {
          params: {
            path: { account_name: accountName },
            query: moveToAnotherAccount
              ? { move_to_another_account: moveToAnotherAccount }
              : undefined,
          },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: (_data, { accountName, moveToAnotherAccount }) => {
      // Invalidate source account
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.status(accountName),
      })
      queryClient.removeQueries({
        queryKey: queryKeys.rentals.current(accountName),
      })
      queryClient.removeQueries({
        queryKey: queryKeys.rentals.fuelCard(accountName),
      })

      // If moving to another account, invalidate that too
      if (moveToAnotherAccount) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.accounts.status(moveToAnotherAccount),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.rentals.current(moveToAnotherAccount),
        })
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all })
    },
  })
}
